import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ArrowUpRight, Bookmark, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { useStudent } from "@/lib/auth";
import { useSavedUniversities } from "@/lib/useSaved";

const PIPELINE = ["New", "Contacted", "Consultation", "Application", "Closed"];

function ProgressTimeline({ status, leadId }) {
  const idx = Math.max(0, PIPELINE.indexOf(status));
  return (
    <div className="mt-7 flex items-start w-full" data-testid={`lead-timeline-${leadId}`}>
      {PIPELINE.map((s, i) => (
        <div key={s} className={`flex items-start ${i < PIPELINE.length - 1 ? "flex-1" : ""}`}>
          <div className="flex flex-col items-center">
            <div className={`h-3 w-3 rounded-full border-2 transition-colors duration-500 ${i <= idx ? "bg-gold border-gold" : "bg-white border-navy/25"}`} />
            <span className={`mt-2.5 text-[10px] uppercase tracking-wider whitespace-nowrap ${i === idx ? "text-navy font-bold" : "text-navy/40"}`}>{s}</span>
          </div>
          {i < PIPELINE.length - 1 && (
            <div className={`h-0.5 flex-1 mx-2 mt-[5px] transition-colors duration-500 ${i < idx ? "bg-gold" : "bg-navy/15"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

const COMPARE_ROWS = [
  { label: "City", render: (u) => u.city || "—" },
  { label: "State", render: (u) => u.state || "—" },
  { label: "University group", render: (u) => u.group || "—" },
  { label: "Popular study areas", render: (u) => (u.popular_study_areas || []).join(", ") || "—" },
  { label: "Why consider", render: (u) => u.why_consider || "—" },
];

function CompareTable({ unis }) {
  return (
    <div className="overflow-x-auto border border-border bg-white" data-testid="profile-compare-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="border-b border-border bg-sand">
            <th className="px-6 py-5 text-left w-44" />
            {unis.map((u) => (
              <th key={u.slug} className="px-6 py-5 text-left align-top">
                <Link to={`/universities/${u.slug}`} className="font-serif text-xl font-medium text-navy hover:text-gold transition-colors leading-snug block">
                  {u.name}
                </Link>
                <span className="text-xs text-navy/50 font-normal">{u.city}, {u.state}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map((row) => (
            <tr key={row.label} className="border-b border-border last:border-0">
              <td className="px-6 py-5 text-xs uppercase tracking-[0.15em] text-navy/45 font-semibold align-top">{row.label}</td>
              {unis.map((u) => (
                <td key={u.slug} className="px-6 py-5 text-navy/75 leading-relaxed align-top max-w-xs">{row.render(u)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StudentProfile() {
  const { student, signOut, signInWithGoogle } = useStudent();
  const { saved, toggle } = useSavedUniversities();
  const location = useLocation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [savedUnis, setSavedUnis] = useState(null);

  const effectiveStudent = location.state?.user || student;

  useEffect(() => {
    if (student === null && !location.state?.user) navigate("/");
  }, [student, location.state, navigate]);

  useEffect(() => {
    if (!effectiveStudent) return;
    api.get("/student/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [effectiveStudent]);

  useEffect(() => {
    if (!effectiveStudent) return;
    api.get("/student/saved").then((r) => setSavedUnis(r.data)).catch(() => setSavedUnis([]));
  }, [effectiveStudent, saved]);

  if (!effectiveStudent) {
    return <div className="min-h-screen bg-cream"><Navbar /></div>;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div data-testid="student-profile-page" className="min-h-screen bg-cream">
      <SEO title="My Profile | Vitaliah" description="Your Vitaliah student profile and guidance requests." />
      <Navbar />
      <section className="bg-navy text-cream relative grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-20">
          <FadeUp><Overline dark>My account</Overline></FadeUp>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-8">
            {effectiveStudent.picture ? (
              <img src={effectiveStudent.picture} alt="" data-testid="profile-avatar" className="h-20 w-20 rounded-full border-2 border-gold object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-full border-2 border-gold bg-navy-light flex items-center justify-center font-serif text-3xl text-gold">
                {(effectiveStudent.name || "S")[0]}
              </div>
            )}
            <div>
              <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight" data-testid="profile-name">{effectiveStudent.name}</h1>
              <p className="mt-2 text-cream/60" data-testid="profile-email">{effectiveStudent.email}</p>
            </div>
            <button
              data-testid="profile-signout-button"
              onClick={handleSignOut}
              className="sm:ml-auto inline-flex items-center gap-2 border border-cream/30 text-cream px-6 py-3 text-sm font-semibold hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <h2 className="font-serif text-3xl font-medium text-navy">Saved universities</h2>
            <p className="mt-3 text-navy/60 text-sm">Bookmark universities anywhere on the site and they'll appear here.</p>
          </FadeUp>
          <div className="mt-8" data-testid="profile-saved-universities">
            {savedUnis === null ? (
              <p className="text-navy/40 text-sm">Loading…</p>
            ) : savedUnis.length === 0 ? (
              <div className="bg-white border border-border p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
                <p className="text-navy/60">No saved universities yet — tap the bookmark on any university card.</p>
                <button
                  data-testid="profile-browse-universities"
                  onClick={() => navigate("/universities")}
                  className="inline-flex items-center gap-2 border border-navy text-navy px-6 py-3 text-sm font-semibold hover:bg-navy hover:text-cream transition-colors shrink-0"
                >
                  Browse Universities <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {savedUnis.map((u) => (
                  <div key={u.slug} data-testid={`saved-uni-${u.slug}`} className="bg-white border border-border p-5 flex items-center gap-4">
                    <Bookmark className="h-4 w-4 text-gold shrink-0" fill="currentColor" />
                    <Link to={`/universities/${u.slug}`} className="flex-1 min-w-0">
                      <p className="font-serif text-lg font-medium text-navy truncate hover:text-gold transition-colors">{u.name}</p>
                      <p className="text-xs text-navy/50">{u.city}, {u.state}</p>
                    </Link>
                    <button
                      data-testid={`remove-saved-${u.slug}`}
                      aria-label="Remove from saved"
                      onClick={() => toggle(u.slug)}
                      className="p-2 text-navy/40 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {savedUnis && savedUnis.length >= 2 && (
            <FadeUp className="mt-12">
              <h3 className="font-serif text-2xl font-medium text-navy">Side-by-side comparison</h3>
              <div className="mt-6">
                <CompareTable unis={savedUnis} />
              </div>
            </FadeUp>
          )}
          {savedUnis && savedUnis.length === 1 && (
            <p className="mt-8 text-sm text-navy/50" data-testid="compare-hint">Save one more university to compare them side by side.</p>
          )}

          <FadeUp className="mt-16">
            <h2 className="font-serif text-3xl font-medium text-navy">My guidance requests</h2>
            <p className="mt-3 text-navy/60 text-sm">Requests submitted with this email address appear here.</p>
          </FadeUp>
          <div className="mt-10" data-testid="profile-leads">
            {leads === null ? (
              <p className="text-navy/40 text-sm">Loading…</p>
            ) : leads.length === 0 ? (
              <div className="bg-white border border-border p-10 text-center">
                <p className="text-navy/60">You haven't submitted a guidance request yet.</p>
                <button
                  data-testid="profile-start-guidance"
                  onClick={() => navigate("/contact")}
                  className="mt-6 inline-flex items-center gap-2 bg-navy text-cream px-8 py-3.5 text-sm font-semibold hover:bg-gold hover:text-navy transition-colors"
                >
                  Get Free Guidance <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {leads.map((l) => (
                  <FadeUp key={l.id}>
                    <div data-testid={`profile-lead-${l.id}`} className="bg-white border border-border p-7">
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-[0.15em] text-navy/45">Submitted</p>
                          <p className="mt-1 text-navy font-medium">{new Date(l.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.15em] text-navy/45">Study interest</p>
                          <p className="mt-1 text-navy">{l.study_interest || "—"}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.15em] text-navy/45">Intake</p>
                          <p className="mt-1 text-navy">{l.intake || "—"}</p>
                        </div>
                      </div>
                      <ProgressTimeline status={l.status} leadId={l.id} />
                      {l.status_history?.length > 0 && (
                        <div className="mt-6 border-t border-border pt-5 space-y-3.5" data-testid={`lead-history-${l.id}`}>
                          {[...l.status_history].reverse().map((h, i) => (
                            <div key={i} className="flex gap-5 text-sm">
                              <span className="text-navy/40 text-xs w-20 shrink-0 pt-0.5">{new Date(h.at).toLocaleDateString()}</span>
                              <div>
                                <span className="font-semibold text-navy text-xs uppercase tracking-wider">{h.status}</span>
                                {h.note && <p className="text-navy/60 mt-1 leading-relaxed">{h.note}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </FadeUp>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
